/**
 * SheetsImportPanel — Google Sheets API 経由でシートを取得 (Sprint BG-DataSettings 20、GAP-A2 close)。
 *
 * 旧 v1 admin の Sheets 取込 (callable fetchSpreadsheet 経由) を v2 で復活。
 * 既存 ImportPage の「2-A. Google Sheets URL から取込」は **公開ウェブ + browser fetch** の経路で、
 * 公開設定なしのシートには使えない。本 panel は service account 経由の callable を使うため、
 * Service Account email に閲覧共有さえあれば private sheet も取込可能。
 *
 * 取得結果は CSV 風 (header + rows) として preview に流すため、`onCsvLoaded(csvText, label)` を
 * callback で受領する (ImportPage 側で setPreview / setCsvText 等を統合)。
 *
 * 入力:
 *   - spreadsheetId (URL から抽出 or 直接入力)
 *   - sheetName (タブ名、例: "Sheet1")
 *
 * 失敗時:
 *   - not-found → 「シートまたはタブが見つかりません」
 *   - permission-denied → 「Service Account 共有未設定」
 *   - その他 → エラー文言そのまま表示
 */

import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { functions } from '../../lib/firebase';
import { extractSheetId } from './sheetsUrl';

/** functions/src/admin/fetchSpreadsheet.ts の入出力 shape (CLAUDE.md boundary 規約で local 宣言)。 */
interface FetchSpreadsheetInput {
  spreadsheetId: string;
  sheetName: string;
}
interface FetchSpreadsheetResult {
  headers: string[];
  rows: string[][];
  totalRows: number;
}

export interface SheetsImportPanelProps {
  /** false の場合、入力 / button を全て disabled (権限なし)。 */
  enabled: boolean;
  /** CSV 取得成功時に呼び出される。ImportPage 側で preview / 投入 flow に流す。 */
  onCsvLoaded(csvText: string, label: string): void;
}

/** Sheets API の string[][] を RFC 4180 minimum 風 CSV に直す (引用符 / カンマ / 改行のエスケープ)。 */
export function rowsToCsv(headers: string[], rows: string[][]): string {
  function esc(cell: string): string {
    if (/[",\r\n]/.test(cell)) {
      return `"${cell.replace(/"/g, '""')}"`;
    }
    return cell;
  }
  const lines: string[] = [];
  lines.push(headers.map(esc).join(','));
  for (const row of rows) lines.push(row.map(esc).join(','));
  return lines.join('\n');
}

export function SheetsImportPanel({ enabled, onCsvLoaded }: SheetsImportPanelProps) {
  const { toast } = useToast();
  const [urlOrId, setUrlOrId] = useState('');
  const [sheetName, setSheetName] = useState('Sheet1');
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{
    headerCount: number;
    rowCount: number;
  } | null>(null);

  async function handleFetch() {
    setError(null);
    setLastResult(null);
    const input = urlOrId.trim();
    if (!input || !sheetName.trim()) return;
    const spreadsheetId = extractSheetId(input) ?? input;
    setFetching(true);
    try {
      const callable = httpsCallable<FetchSpreadsheetInput, FetchSpreadsheetResult>(
        functions,
        'fetchSpreadsheet',
      );
      const res = await callable({ spreadsheetId, sheetName: sheetName.trim() });
      const { headers, rows, totalRows } = res.data;
      if (headers.length === 0) {
        setError('シートが空です (header 行なし)');
        toast('warning', 'シートが空です');
        return;
      }
      const csv = rowsToCsv(headers, rows);
      const label = `(Sheets API: ${spreadsheetId} / ${sheetName})`;
      onCsvLoaded(csv, label);
      setLastResult({ headerCount: headers.length, rowCount: totalRows });
      toast('success', `Sheets から ${totalRows} 行を取得`);
    } catch (e) {
      const msg = (e as Error).message ?? String(e);
      setError(`Sheets 取得失敗: ${msg}`);
      toast('error', `Sheets 取得失敗`);
    } finally {
      setFetching(false);
    }
  }

  return (
    <Card padding="lg" className="mb-4">
      <h2 className="text-base font-bold text-slate-900 mb-3">
        2-B. Sheets API 経由で取込 (private 可、GAP-A2)
      </h2>
      <p className="text-xs text-slate-500 mb-3">
        Service Account email に閲覧共有したシートを Sheets API で取得 (公開設定不要)。 URL
        を貼付するか sheet ID を直接入力。タブ名は既定 <code>Sheet1</code>。
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <input
          type="text"
          value={urlOrId}
          onChange={(e) => setUrlOrId(e.target.value)}
          disabled={!enabled || fetching}
          placeholder="https://docs.google.com/spreadsheets/d/<id>/edit or <id>"
          aria-label="spreadsheet URL or ID"
          className="md:col-span-2 min-h-touch px-3 border border-slate-300 rounded text-sm font-mono"
        />
        <input
          type="text"
          value={sheetName}
          onChange={(e) => setSheetName(e.target.value)}
          disabled={!enabled || fetching}
          placeholder="Sheet1"
          aria-label="sheet name"
          className="min-h-touch px-3 border border-slate-300 rounded text-sm font-mono"
        />
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => void handleFetch()}
        disabled={!enabled || fetching || !urlOrId.trim() || !sheetName.trim()}
      >
        {fetching ? '取得中…' : 'API で取得して preview'}
      </Button>
      {error && (
        <p className="text-sm text-danger-700 mt-3" role="alert">
          {error}
        </p>
      )}
      {lastResult && !error && (
        <p className="text-xs text-slate-600 mt-3">
          取得完了: header {lastResult.headerCount} 列 / data {lastResult.rowCount} 行
        </p>
      )}
    </Card>
  );
}
